import React from 'react';
import { pickLocalizedText } from '../../../utils/localizedContent';

export default function SwissCompetitionView({ bracket, hasEventsPermission, onNextSwissRound, onSelectMatch, actionLoading, lang, participantsById = {} }) {
  const rounds = Object.entries(bracket?.rounds || {}).sort(([a], [b]) => Number(a) - Number(b));
  const currentRoundKey = String(bracket?.current_round || 1);
  const currentRoundMatches = bracket?.rounds?.[currentRoundKey] || [];
  const currentRoundComplete = currentRoundMatches.length > 0 && currentRoundMatches.every((m) => m.status === 'completed');
  const canGenerateNext = hasEventsPermission &&
    bracket?.status === 'active' &&
    currentRoundComplete &&
    (bracket?.current_round || 1) < (bracket?.total_rounds || 1);
  const text = (es, en) => pickLocalizedText(lang, es, en);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
      <div className="xl:col-span-2 space-y-4">
        <div className="mb-2 flex flex-wrap items-center gap-3 text-sm">
          <span className="px-2 py-1 rounded bg-secondary text-primary">
            {text('Ronda actual', 'Current round')}: {bracket?.current_round}/{bracket?.total_rounds}
          </span>
          {canGenerateNext && (
            <button
              onClick={onNextSwissRound}
              disabled={actionLoading === 'swiss-next-round'}
              className="btn-primary px-4 py-1.5 text-xs"
            >
              {text('Generar siguiente ronda', 'Generate next round')}
            </button>
          )}
        </div>
        {rounds.map(([roundNum, matches]) => (
          <div key={roundNum} className="bg-card border border-border rounded-xl p-4">
            <h4 className="text-primary font-semibold mb-3">{text('Ronda', 'Round')} {roundNum}</h4>
            <div className="space-y-2">
              {matches.map((match) => (
                <MatchCard
                  key={match.match_id}
                  match={match}
                  onSelectMatch={onSelectMatch}
                  hasEventsPermission={hasEventsPermission}
                  lang={lang}
                  participantsById={participantsById}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="bg-card border border-border rounded-xl p-4 h-fit">
        <h4 className="text-primary font-semibold mb-3">{text('Clasificacion suiza', 'Swiss Standings')}</h4>
        <div className="space-y-2">
          {(bracket?.standings || []).map((s) => {
            const p = s.player || {};
            const full = participantsById[p.user_id] || {};
            const initial = (full.name || p.name || '?').charAt(0).toUpperCase();
            const avatarUrl = full.avatar;
            return (
              <div key={p.user_id} className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-[#111827] border border-white/10 overflow-hidden flex items-center justify-center text-[11px] text-white">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={full.name || p.name || 'Player'} className="w-full h-full object-cover" />
                    ) : (
                      initial
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-foreground text-sm truncate">#{s.position} {p.name}</p>
                    <p className="text-muted-foreground text-xs">{s.wins}W-{s.draws || 0}D-{s.losses}L</p>
                  </div>
                </div>
                <span className="text-[#C9A962] font-bold text-sm">{s.points || 0}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MatchCard({ match, onSelectMatch, hasEventsPermission, lang, participantsById = {} }) {
  const canReport = hasEventsPermission &&
    match.status === 'pending' &&
    match.player1?.user_id &&
    match.player2?.user_id &&
    match.player1?.name !== 'BYE' &&
    match.player2?.name !== 'BYE';
  const text = (es, en) => pickLocalizedText(lang, es, en);
  const p1 = match.player1 || {};
  const p2 = match.player2 || {};
  const full1 = participantsById[p1.user_id] || {};
  const full2 = participantsById[p2.user_id] || {};
  const avatar1 = full1.avatar;
  const avatar2 = full2.avatar;
  const initial1 = (full1.name || p1.name || 'T').charAt(0).toUpperCase();
  const initial2 = (full2.name || p2.name || 'T').charAt(0).toUpperCase();
  return (
    <div className={`bg-card border rounded-xl overflow-hidden ${match.status === 'completed' ? 'border-emerald-500/30' : 'border-border'}`}>
      <div className="p-3 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-full bg-[#111827] border border-white/10 overflow-hidden flex items-center justify-center text-[11px] text-white">
            {avatar1 ? (
              <img src={avatar1} alt={full1.name || p1.name || 'Player 1'} className="w-full h-full object-cover" />
            ) : (
              initial1
            )}
          </div>
          <span className="text-foreground text-sm truncate">{p1.name || 'TBD'}</span>
        </div>
        <span className="text-muted-foreground text-sm">{match.status === 'completed' ? match.score_p1 : '-'}</span>
      </div>
      <div className="border-t border-border/60" />
      <div className="p-3 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-full bg-[#111827] border border-white/10 overflow-hidden flex items-center justify-center text-[11px] text-white">
            {avatar2 ? (
              <img src={avatar2} alt={full2.name || p2.name || 'Player 2'} className="w-full h-full object-cover" />
            ) : (
              initial2
            )}
          </div>
          <span className="text-foreground text-sm truncate">{p2.name || 'TBD'}</span>
        </div>
        <span className="text-muted-foreground text-sm">{match.status === 'completed' ? match.score_p2 : '-'}</span>
      </div>
      {canReport && (
        <div className="border-t border-border p-2">
          <button onClick={() => onSelectMatch(match)} className="w-full text-xs text-primary hover:bg-primary/10 py-1 rounded">
            {text('Reportar resultado', 'Report Result')}
          </button>
        </div>
      )}
    </div>
  );
}
